import { EventEmitter, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import {
  Firestore,
  collectionData,
  collection,
  CollectionReference,
  doc,
  docData,
  updateDoc,
  deleteField,
} from "@angular/fire/firestore";
import { orderBy, query } from "@firebase/firestore";

import { Activity } from "../../models/activities/activities.interface";
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { getLearnerAttentionState, needsLearnerAction, getLmsRetakeState } from "./activity-display-state";


@Injectable({
  providedIn: "root",
})
export class ActivityService {
  activities$: Observable<Activity[]>;
  activityTypePrettyMap = {
    lms: "LMS",
    lmsQuiz: "LMS Quiz",
    article: "Article",
    video: "Video",
    booster: "Booster",
    observeShadow: "Observation/Shadow",
    coachObservation: "Coach Observation",
    surveys: "Surveys",
    webinar: "Webinar",
    prerecordedLearnerVideos: "Prerecorded Learner Videos",
    recording: "Recording",
    other: "Other"
  }

  activityToCreate = new EventEmitter<any>();
  usersSelectedForActivity = [];

  // Only activities owned by this agency are gated by the prerequisite chain
  prerequisiteChainAgencyId = 'YdvxhXOWHz8jVZgnlAyh';

  // Skills (by name) of the chain agency whose activities are not forced into
  // sequence: nothing is locked. Matched by name, as skill ids differ per agency.
  prerequisiteChainExemptSkills = ['Role Clarification'];

  // Temporary: sequencing is paused for the chain agency, so every skill is
  // treated as exempt (nothing locked, no sequence notice). Chain sort order
  // still applies. Set back to false to turn sequencing on again.
  prerequisiteChainPaused = true;

  isChainExemptSkill(skill: any): boolean {
    if (this.prerequisiteChainPaused) {
      return true;
    }
    const name = (skill?.name || '').trim().toLowerCase();
    return !!name && this.prerequisiteChainExemptSkills.some((s) => s.toLowerCase() == name);
  }

  // Prerequisite chain (level codes: nu = 101, iu = 201, au = 301):
  // lms nu -> pyi nu -> lms iu -> pyi iu -> (recording + lms au) -> pyi au
  // An entry with a null level matches that type at any level.
  private prerequisiteChain = [
    { type: 'lms', level: 'nu', requires: null },
    { type: 'pyi', level: 'nu', requires: { type: 'lms', level: 'nu' } },
    { type: 'lms', level: 'iu', requires: { type: 'pyi', level: 'nu' } },
    { type: 'pyi', level: 'iu', requires: { type: 'lms', level: 'iu' } },
    { type: 'recording', level: null, requires: { type: 'pyi', level: 'iu' } },
    { type: 'lms', level: 'au', requires: { type: 'pyi', level: 'iu' } },
    { type: 'pyi', level: 'au', requires: { type: 'lms', level: 'au' } },
  ];

  // Sets activity.locked on every activity in the list based on the prerequisite chain.
  // Pass the full unfiltered list so completed prerequisites hidden from display still count.
  // `skill` is the skill the list belongs to; an exempt skill leaves everything unlocked.
  applyPrerequisiteLocks(activities: any[], skill?: any) {
    const exempt = this.isChainExemptSkill(skill);
    activities.forEach((activity: any) => {
      activity.locked = exempt ? false : this.isActivityLocked(activity, activities);
    });
  }

  // Assigns activity.sortOrder for the dashboard/skill-view activity lists.
  // Chain-agency activities sort in prerequisite-chain order (dueOn breaks ties,
  // then attempt order so LMS retakes sharing a due date read oldest to newest);
  // other agencies keep their dueOn order.
  applyChainSortOrder(activities: any[]) {
    activities.forEach((activity: any) => {
      if (activity.agencyId == this.prerequisiteChainAgencyId) {
        const index = this.prerequisiteChain.findIndex((r) =>
          r.type == activity.type && (r.level == null || r.level == activity.level)
        );
        const position = index == -1 ? this.prerequisiteChain.length : index;
        activity.sortOrder = position.toString().padStart(2, '0') + '_' + (activity.dueOn || '') + '_' + this.attemptSortKey(activity);
      } else {
        activity.sortOrder = activity.dueOn;
      }
    });
  }

  // Sortable attempt order: finished attempts by completion time (oldest first),
  // then unfinished ones last. completionDate formats vary, so normalize to ms.
  private attemptSortKey(activity: any): string {
    const finished = activity.status == 'Completed' ? new Date(activity.completionDate || 0).getTime() : NaN;
    return isNaN(finished) || activity.status != 'Completed' ? '9'.repeat(15) : finished.toString().padStart(15, '0');
  }

  // Sets activity.attentionState / activity.needsLearnerAction / activity.retakeState (see activity-display-state.ts).
  // Run after applyPrerequisiteLocks: a locked activity is never an attention row.
  applyAttentionState(activities: any[]) {
    activities.forEach((activity: any) => {
      activity.attentionState = getLearnerAttentionState(activity);
      activity.needsLearnerAction = needsLearnerAction(activity);
      activity.retakeState = getLmsRetakeState(activity, activities);
    });
  }

  // Stamps the first time the learner opens their recording or PYI report.
  // Callers guard on ownership and on the field being unset.
  markReportViewed(userId: string, skillId: string, activityId: string): Promise<void> {
    const activityRef = doc(this.firestore, `userProfiles/${userId}/skills/${skillId}/activities/${activityId}`);
    return updateDoc(activityRef, { reportViewedAt: new Date().toISOString() });
  }

  // Background survey before upload (see bcs-survey.ts): the activity id becomes the
  // recording activity's sguid, and bcsCompletedAt marks the survey as submitted (which
  // also starts the activity: In Progress, "Awaiting Recording").
  setBackgroundSurveyFields(userId: string, skillId: string, activityId: string,
    fields: { sguid?: string; bcsCompletedAt?: string; status?: string; startDate?: string; reviewStatus?: string }): Promise<void> {
    const activityRef = doc(this.firestore, `userProfiles/${userId}/skills/${skillId}/activities/${activityId}`);
    return updateDoc(activityRef, fields);
  }

  // Automated reviews have no surveyresponses doc, so coach edits to the AI
  // learner report live on the activity instead.
  saveCoachesReport(userId: string, skillId: string, activityId: string, report: string): Promise<void> {
    const activityRef = doc(this.firestore, `userProfiles/${userId}/skills/${skillId}/activities/${activityId}`);
    return updateDoc(activityRef, { coachesReport: report });
  }

  // Removes the coach-edited report so the AI report shows through again.
  clearCoachesReport(userId: string, skillId: string, activityId: string): Promise<void> {
    const activityRef = doc(this.firestore, `userProfiles/${userId}/skills/${skillId}/activities/${activityId}`);
    return updateDoc(activityRef, { coachesReport: deleteField() });
  }

  isActivityLocked(activity: any, activities: any[]): boolean {
    if (activity.agencyId != this.prerequisiteChainAgencyId) {
      return false;
    }

    if (activity.status == 'Completed') {
      return false;
    }

    const rule = this.prerequisiteChain.find((r) =>
      r.type == activity.type && (r.level == null || r.level == activity.level)
    );

    // Activity types/levels outside the chain are never locked
    if (!rule || !rule.requires) {
      return false;
    }

    const prerequisiteCompleted = activities.some((a: any) =>
      a.type == rule.requires.type &&
      a.level == rule.requires.level &&
      a.status == 'Completed' &&
      // An LMS completed with a score under 80 is a failed attempt ("Retake Activity")
      // and doesn't satisfy the prerequisite; a passing LMS at the same level does.
      !(a.type == 'lms' && a.score?.score < 80)
    );

    return !prerequisiteCompleted;
  }

  loading: boolean;

  constructor(private firestore: Firestore) {}

  public getActivities(): Observable<Activity[]> {
    const notesRef = collection(this.firestore, "activities");
    return collectionData(notesRef, { idField: "id" }) as Observable<Activity[]>;
  }


  public getActivityById(id: string): Observable<Activity> {
    const bookRef = doc(this.firestore, `activities/${id}`);
    return docData(bookRef, { idField: 'id' }) as Observable<Activity>;
  }



  saveActivity(activity: any) {
    const functions = getFunctions();

    const addActivity = httpsCallable(functions, 'addActivity');

    return addActivity({body: activity}); 
  }

  updateActivityAction(activity: any, userId: string, skillId: string, activityId: string) {
    const functions = getFunctions();

    const updateActivityAction = httpsCallable(functions, 'updateActivityAction');

    return updateActivityAction({body: activity, userId: userId, skillId: skillId, activityId: activityId}); 
  }


  updateActivityDueOn(activity: any, userId: string, skillId: string, activityId: string) {
    const functions = getFunctions();

    const updateActivityDueOn = httpsCallable(functions, 'updateActivityDueOn');

    return updateActivityDueOn({body: activity, userId: userId, skillId: skillId, activityId: activityId}); 
  }

  addDuplicateLMSActivity(userId: string, skillId: string, activityId: string) {
    const functions = getFunctions();

    const addDuplicateLMSActivity = httpsCallable(functions, 'addDuplicateLMSActivity');

    return addDuplicateLMSActivity({userId, skillId, activityId}); 
  }

  addNote(userId: string, skillId: string, activityId: string, noteModel: any) {
    const functions = getFunctions();

    const addNote = httpsCallable(functions, 'addNote');

    return addNote({userId, skillId, activityId, noteModel}); 
  }

  canUserStartActivity(userId: string, skillId: string, activity: any) {
    const functions = getFunctions();

    const canUserStartActivity = httpsCallable(functions, 'canUserStartActivity');

    return canUserStartActivity({userId, skillId, activity}); 
  }

  getUsersToAddActivity() {
    // If the array is not empty, get the array from local storage and set the array and return it
      const arrStr = localStorage.getItem('arrOfUsersToAddActivity');
      if (!arrStr) {
        return [];
      }
      return JSON.parse(arrStr);

  }

  async markActivityCompletedCallable(userId: string, skillId: string, activityId: string) {
    console.log({userId, skillId, activityId})
    const functions = getFunctions();
  
    const markActivityCompletedCallable = httpsCallable(functions, 'markActivityCompletedCallable');
  
    return markActivityCompletedCallable({userId, skillId, activityId}); 
  }
  


  setUsersToAddActivity(arrOfUsers: any[]) {
    // Sets the array of users to the service variable
    // Also sets the array to local storage
    localStorage.setItem('arrOfUsersToAddActivity', JSON.stringify(arrOfUsers));
  }

  clearLocalStorage() {
    localStorage.clear();
  }

  reportFilters = {
    // User-level filters
    agencyIds: ['YoLvHFX7DSukoVeIXT5i'],
    //agencyIds: ['9Rz8evD3YGrpP1qj2aIl'],
    //userIds: ['iaDXa4joeNBXpzUBgzRHzXGAyfyt'],
    //userProperties: {
    //  jobRole: 'Praxus Group NC',
    //},
  
    // Activity filters
    types: ['lms'],
    levels: ['nu', 'iu', 'au'],
    //statuses: ['Completed'],
    
    // Date range filters
    //startDateFrom: '2024-01-01',
    //startDateTo: '2024-12-31',
    //dueDateFrom: '2024-01-01',
    //dueDateTo: '2024-12-31',
    //completionDateFrom: '2024-01-01',
    //completionDateTo: '2024-12-31',
  
    // Score filter
   // minScore: 50
  };

  public async getActivitiesReporting(agencyId: string, skillId: string, coachId?: string): Promise<any>{
    const functions = getFunctions();
  
    const getActivitiesReporting = httpsCallable(functions, 'getActivitiesReporting');
    
    var reportFilters = {
      agencyIds: [agencyId],
      skillIds: [skillId],
      userProperties: coachId ? {
        'coach.id': coachId
      } : undefined
    }

    //this.reportFilters.userIds = [userId];
    var results =  await getActivitiesReporting({
      filters: reportFilters,
      options: {
        includeAllActivities: false
      }
    });

    return results.data;
  }

  public async getUserActivitiesSummary(agencyId: string, skillId?: string): Promise<any>{


    var reportFilters = {
        agencyIds: [agencyId],
        skillIds: [skillId],
       // userIds: ["0BXaI7ffghSYCC9TeTTwyXdd8Yy9"],
    }

    const functions = getFunctions();
  
    const getUserActivitiesSummary = httpsCallable(functions, 'getUserActivitiesReporting');
    
    //this.reportFilters.userIds = [userId];
    var results =  await getUserActivitiesSummary({
      filters: reportFilters,
      options: {
        includeAllActivities: false
      }
    });

    return results.data;
  }

  public async closeActivitiesForAgencyUsers(agencyId: string): Promise<any>{
    const functions = getFunctions();
  
    const closeActivitiesForAgencyUsers = httpsCallable(functions, 'closeActivitiesForAgencyUsers');
    
    //this.reportFilters.userIds = [userId];
    var results =  await closeActivitiesForAgencyUsers({
      agencyId: agencyId,
    });

    return results.data;
  }

  public async closeActivityForUser(userId: string, skillId: string, activityId: string): Promise<any>{
    const functions = getFunctions();
  
    const closeActivityForUser = httpsCallable(functions, 'closeActivity');
    
    var results =  await closeActivityForUser({
      userId: userId,
      skillId: skillId,
      activityId: activityId,
      reason: "Closed by user"
    });

    return results.data;
  }

  public async markActivityCompletedForUser(userId: string, skillId: string, activityId: string): Promise<any>{
    const functions = getFunctions();
  
    const markActivityCompletedForUser = httpsCallable(functions, 'markActivityCompletedCallable');
    
    var results =  await markActivityCompletedForUser({
      userId: userId,
      skillId: skillId,
      activityId: activityId
    });

    return results.data;
  }

  public async getUsersActivitiesSummary(agencyId: string, skillId: string, coachId?: string): Promise<any>{
    const functions = getFunctions();
  
    const getUsersActivitiesSummary = httpsCallable(functions, 'getUsersActivitiesSummary');
    
    //this.reportFilters.userIds = [userId];
    var reportFilters = {
      agencyIds: [agencyId],
      skillId: skillId,
      userProperties: coachId ? {
        'coach.id': coachId
      } : undefined
    }
    var results =  await getUsersActivitiesSummary({filters: reportFilters});

    return results.data;
  }

  // e.g. saveSelectedNextSteps(this.activityOwnerId, this.skillId, this.activityId, selectedSteps);
  public async saveNextSteps(userId: string, skillId: string, activityId: string, selectedSteps: any[]): Promise<any> {
    const functions = getFunctions();

    const saveNextSteps = httpsCallable(functions, 'saveNextSteps');

    console.log("Saving next steps:", { userId, skillId, activityId, selectedSteps });
    var results = await saveNextSteps({
      userId: userId,
      skillId: skillId,
      activityId: activityId,
      nextSteps: selectedSteps
    });

    console.log("Save next steps results:", results.data);
    return results.data;
  }

}
